import axios from 'axios';
import { Formik } from 'formik';
import React, { useEffect } from 'react';
import { ActivityIndicator, View } from 'react-native';
import * as Yup from 'yup';

import { CompanyClient } from '../../../api';
import tw from '../../../config/tailwind';
import { useToasts } from '../../../hooks/useToasts';
import {
  CompanyResponse,
  UpdateCompanyPayload,
} from '../../../shared/types/companies';
import Button from '../../shared/Button';
import Col from '../../shared/Col';
import DefaultModal from '../../shared/DefaultModal';
import InputText from '../../shared/InputText';
import InputValidationMessage from '../../shared/InputValidationMessage';
import Row from '../../shared/Row';
import InputCheckbox from '../../shared/InputCheckbox';

interface EditCompanyModalProps {
  visible: boolean;
  setVisible: (v: boolean) => void;
  company: CompanyResponse;
  onUpdated: (company: CompanyResponse) => void;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  visible,
  setVisible,
  company,
  onUpdated,
}) => {
  const { addToast, addUnhandledErrorToast } = useToasts();

  const initialValues: UpdateCompanyPayload = {
    name: company.name,
    rebateBackdate: company.rebateBackdate ?? false
  };

  const schema: Yup.SchemaOf<UpdateCompanyPayload> = Yup.object().shape({
    name: Yup.string().required(),
    rebateBackdate: Yup.boolean(),
  });

  const onSubmit = async (values: UpdateCompanyPayload) => {
    try {
      const newCompany = await CompanyClient.update(company.id, values);

      addToast({
        title: 'Company updated',
        description: `We've saved the company '${newCompany.name}'`,
        type: 'success',
      });

      onUpdated(newCompany);
      setVisible(false);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        addToast({
          title: 'Duplicate company name',
          description:
            'A company with this name already exists, please try something else.',
          type: 'error',
        });
      } else {
        addUnhandledErrorToast(e);
      }
    }
  };

  return (
    <DefaultModal
      setVisible={setVisible}
      visible={visible}
      title='Edit company'
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          values,
          errors,
          isSubmitting,
        }) => (
          <View style={tw`mt-4`}>
            <Row>
              <Col style={tw`w-1/2`}>
                <InputText
                  value={values.name}
                  setValue={handleChange('name')}
                  onBlur={handleBlur('name')}
                  placeholder='Name'
                  hasError={!!errors.name}
                  label='Name'
                  required
                />
                {errors.name && (
                  <InputValidationMessage>{errors.name}</InputValidationMessage>
                )}
              </Col>
            </Row>
            <Row>
              <Col style={tw`w-1/2`}>
                <InputCheckbox 
                  label={`Allow backdated rebate application?`}
                  value={values.rebateBackdate ?? false}
                  setValue={() => setFieldValue('rebateBackdate', !values.rebateBackdate)}
                />
              </Col>
            </Row>
            <View style={tw`flex flex-row items-center justify-end mt-4`}>
              {isSubmitting && <ActivityIndicator style={tw`mr-4`} />}
              <Button onPress={handleSubmit} disabled={isSubmitting}>
                Save
              </Button>
            </View>
          </View>
        )}
      </Formik>
    </DefaultModal>
  );
};

export default EditCompanyModal;
